import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { CompareToggle, LyCell, lyWindow, type CompareAlign } from './compare'

// Customer Service -> Accommodation (2026-10-23, Kathryn Watson plan §1.4).
// Camping-coach / Station House lets from cache.AccommodationBookings (Colin's
// ACC_010 logic, Sync Hub job 38 nightly 05:30) via /api/customer-service/accommodation:
// a week-by-property calendar aligned to Fridays, the arrivals list, season totals
// with Compare against season (aligned by week — lets run Friday to Friday), and
// PDF / XLS downloads of the bookings in the window.

interface AccRow {
  booking_ref: number; line_seq: number; item: string | null; property: string; is_let: boolean; arrival_date: string; departure_date: string | null
  nights: number; qty: number; price: number; adults: number; children: number; babies: number; dogs: number; key_code: string | null
  account_name: string | null; customer_name: string | null; mobile: string | null; home_tel: string | null; email: string | null
  notes: string | null; booked_on: string | null; status: string; is_internet: boolean | null; cancelled_on: string | null; booking_total: number; paid_so_far: number
}
interface Cell { booking_ref: number; customer: string | null; price: number; nights: number; arrival_date: string; status: string; continues: boolean }
interface Week { friday: string; cells: Record<string, Cell | null> }
interface PropTotal { property: string; lets: number; nights: number; revenue: number; extras: number; weeks_available: number; weeks_let: number; occupancy: number }
interface Block {
  from: string; to: string; season: string
  totals: { lets: number; nights: number; revenue: number; extras: number; total: number; adults: number; children: number; babies: number; dogs: number; paid: number; outstanding: number; weeks: number }
  by_property: PropTotal[]; weeks: Week[]; rows: AccRow[]
}
interface Payload { current: Block; last_season: Block | null; windows: { from: string; to: string; ly_from: string; ly_to: string; season: string; ly_season: string }; generated_at: string | null; window_from: string | null; window_to: string | null; cache_rows: number }

const GREEN = '#0b3d2e'
const TH: CSSProperties = { textAlign: 'left', padding: '6px 8px', color: '#6b6b6b', fontWeight: 500, fontSize: '0.78rem', borderBottom: '1px solid #e5e1d6', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#fff' }
const TD: CSSProperties = { padding: '6px 8px', fontSize: '0.8rem', borderBottom: '1px solid #f0ece1', verticalAlign: 'top' }
const R: CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }
const gbp = (n: number) => `£${n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
const gbp2 = (n: number) => `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const int = (n: number) => n.toLocaleString('en-GB')
const dmy = (iso: string | null | undefined, weekday = false) => iso ? new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: weekday ? 'short' : undefined, day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—'
const when = (iso: string | null) => iso ? new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'
const statusLabel = (s: string) => s === 'B' ? 'Booked' : s === 'C' ? 'Completed' : s
const SHORT: Record<string, string> = { 'Goathland Camping Coach': 'Goathland CC', 'Grosmont Station House': 'Grosmont SH', 'Levisham Camping Coach': 'Levisham CC' }

function seasonDefault(): { from: string; to: string } {
  const d = new Date(); const y = d.getUTCMonth() + 1 >= 3 ? d.getUTCFullYear() : d.getUTCFullYear() - 1
  const end = new Date(Date.UTC(y + 1, 2, 0))
  return { from: `${y}-03-01`, to: `${y + 1}-${String(end.getUTCMonth() + 1).padStart(2, '0')}-${String(end.getUTCDate()).padStart(2, '0')}` }
}

export function AccommodationView() {
  const def = useMemo(seasonDefault, [])
  const [from, setFrom] = useState(def.from)
  const [to, setTo] = useState(def.to)
  const [compare, setCompare] = useState(false)
  const [align, setAlign] = useState<CompareAlign>('week')
  const [data, setData] = useState<Payload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [section, setSection] = useState<'calendar' | 'arrivals'>('calendar')
  const [q, setQ] = useState('')

  useEffect(() => {
    let live = true
    setLoading(true)
    const qs = new URLSearchParams({ from, to, ...(compare ? { compare: 'ly', align } : {}) }).toString()
    fetch(`/api/customer-service/accommodation?${qs}`)
      .then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`) } return r.json() as Promise<Payload> })
      .then((d) => { if (live) { setData(d); setError(null) } })
      .catch((e) => { if (live) setError(e instanceof Error ? e.message : String(e)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [from, to, compare, align])

  const cur = data?.current ?? null
  const ly = data?.last_season ?? null
  const windows = useMemo(() => lyWindow(from, to, align), [from, to, align])
  const empty = !loading && !error && (data?.cache_rows ?? 0) === 0
  const rows = useMemo(() => {
    const all = cur?.rows ?? []
    const ql = q.trim().toLowerCase()
    return ql ? all.filter((r) => `${r.booking_ref} ${r.property} ${r.item ?? ''} ${r.customer_name ?? ''} ${r.account_name ?? ''} ${r.key_code ?? ''} ${r.notes ?? ''} ${r.email ?? ''} ${r.mobile ?? ''}`.toLowerCase().includes(ql)) : all
  }, [cur, q])
  const today = new Date().toISOString().slice(0, 10)

  const label: CSSProperties = { fontSize: '0.8rem', color: '#6b6b6b', display: 'flex', flexDirection: 'column', gap: 2 }
  const dateInput: CSSProperties = { padding: '6px 10px', fontSize: '0.85rem', border: '1px solid #ccc', borderRadius: 8 }
  const btn: CSSProperties = { background: GREEN, color: '#fff', border: `1px solid ${GREEN}`, borderRadius: 6, padding: '6px 12px', fontWeight: 700, fontSize: '0.8rem', textDecoration: 'none', whiteSpace: 'nowrap' }
  const pill = (k: 'calendar' | 'arrivals', text: string) => (
    <button type="button" onClick={() => setSection(k)} aria-pressed={section === k}
      style={{ background: section === k ? GREEN : '#fff', color: section === k ? '#fff' : GREEN, border: `1px solid ${GREEN}`, borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem' }}>{text}</button>
  )
  const kpi = (title: string, v: number, lyV: number | undefined, fmt: (n: number) => string) => (
    <div style={{ background: '#fff', border: '1px solid #e5e1d6', borderRadius: 8, padding: '8px 14px', minWidth: 120 }}>
      <div style={{ fontSize: '0.72rem', color: '#6b6b6b' }}>{title}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: GREEN, fontVariantNumeric: 'tabular-nums' }}>{fmt(v)}</div>
      {compare && <div style={{ fontSize: '0.75rem' }}><LyCell current={v} ly={lyV} fmt={fmt} /></div>}
    </div>
  )

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <label style={label}>Arrivals from<input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={dateInput} /></label>
        <label style={label}>to<input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={dateInput} /></label>
        <button type="button" onClick={() => { setFrom(def.from); setTo(def.to) }} style={{ ...btn, background: '#fff', color: GREEN }}>This season</button>
        <CompareToggle on={compare} onToggle={() => setCompare((v) => !v)} align={align} onAlign={setAlign} windows={compare ? windows : null} />
        <span style={{ flex: 1 }} />
        <a href={`/api/customer-service/accommodation/pdf?from=${from}&to=${to}`} style={btn} download>Download PDF</a>
        <a href={`/api/customer-service/accommodation/xlsx?from=${from}&to=${to}`} style={{ ...btn, background: '#fff', color: GREEN }} download>Download XLS</a>
      </div>

      {error && <div className="placeholder" style={{ borderColor: '#a33' }}><strong>Couldn&apos;t load accommodation bookings</strong><p style={{ color: '#a33', fontSize: '0.85rem' }}>{error}</p></div>}
      {empty ? (
        <div className="placeholder"><strong>Awaiting first refresh</strong><p>The accommodation cache is empty — Sync Hub job 38 <code>Refresh-AccommodationCourses</code> runs nightly at 05:30.</p></div>
      ) : cur && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            {kpi('Lets', cur.totals.lets, ly?.totals.lets, int)}
            {kpi('Nights', cur.totals.nights, ly?.totals.nights, int)}
            {kpi('Let revenue', cur.totals.revenue, ly?.totals.revenue, gbp)}
            {kpi('Extras (dogs)', cur.totals.extras, ly?.totals.extras, gbp)}
            {kpi('Guests', cur.totals.adults + cur.totals.children + cur.totals.babies, ly ? ly.totals.adults + ly.totals.children + ly.totals.babies : undefined, int)}
            {kpi('Paid so far', cur.totals.paid, ly?.totals.paid, gbp)}
            {kpi('Outstanding', cur.totals.outstanding, ly?.totals.outstanding, gbp)}
          </div>

          <div style={{ overflowX: 'auto', border: '1px solid #e5e1d6', borderRadius: 8, background: '#fff', marginBottom: 12 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 700 }}>
              <thead><tr>
                <th style={TH}>Property</th><th style={{ ...TH, ...R }}>Lets</th><th style={{ ...TH, ...R }}>Nights</th><th style={{ ...TH, ...R }}>Let revenue</th><th style={{ ...TH, ...R }}>Extras</th><th style={{ ...TH, ...R }}>Weeks let</th><th style={{ ...TH, ...R }}>Occupancy</th>
                {compare && <th style={{ ...TH, ...R, color: '#8a8577' }}>Last season lets · £</th>}
              </tr></thead>
              <tbody>
                {cur.by_property.map((p) => {
                  const lp = ly?.by_property.find((x) => x.property === p.property)
                  return (
                    <tr key={p.property}>
                      <td style={{ ...TD, fontWeight: 600 }}>{p.property}</td>
                      <td style={{ ...TD, ...R }}>{int(p.lets)}</td><td style={{ ...TD, ...R }}>{int(p.nights)}</td><td style={{ ...TD, ...R }}>{gbp(p.revenue)}</td><td style={{ ...TD, ...R }}>{gbp(p.extras)}</td>
                      <td style={{ ...TD, ...R }}>{p.weeks_let} / {p.weeks_available}</td><td style={{ ...TD, ...R }}>{Math.round(p.occupancy * 100)}%</td>
                      {compare && <td style={{ ...TD, ...R }}><LyCell current={p.lets} ly={lp?.lets} fmt={int} /> · <LyCell current={p.revenue} ly={lp?.revenue} fmt={gbp} /></td>}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'inline-flex', gap: 6 }} role="group" aria-label="Section">{pill('calendar', 'Week calendar')}{pill('arrivals', `Arrivals (${cur.rows.filter((r) => r.is_let).length})`)}</div>
            {section === 'arrivals' && <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search booking, name, key code, notes…" aria-label="Search arrivals"
              style={{ flex: '1 1 280px', maxWidth: 420, padding: '6px 10px', fontSize: '0.85rem', border: '1px solid #ccc', borderRadius: 8 }} />}
            <span style={{ fontSize: '0.75rem', color: '#8a8577', marginLeft: 'auto' }}>
              {loading ? 'Loading…' : <>Cache {data?.window_from ? `${dmy(data.window_from)} – ${dmy(data.window_to)}` : ''} · refreshed {when(data?.generated_at ?? null)} · weeks run Friday to Friday</>}
            </span>
          </div>

          {section === 'calendar' ? (
            <div style={{ overflowX: 'auto', maxHeight: '62vh', overflowY: 'auto', border: '1px solid #e5e1d6', borderRadius: 8, background: '#fff' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
                <thead><tr><th style={TH}>Week (Fri)</th>{cur.by_property.map((p) => <th key={p.property} style={TH}>{p.property}</th>)}</tr></thead>
                <tbody>
                  {cur.weeks.map((w) => {
                    const past = w.friday < today && w.friday < today.slice(0, 10)
                    const isCurrent = w.friday <= today && today < new Date(new Date(`${w.friday}T00:00:00Z`).getTime() + 7 * 86_400_000).toISOString().slice(0, 10)
                    return (
                      <tr key={w.friday} style={{ background: isCurrent ? '#fffbe6' : undefined }}>
                        <td style={{ ...TD, whiteSpace: 'nowrap', fontWeight: isCurrent ? 700 : 500, color: past ? '#8a8577' : '#222' }}>{dmy(w.friday, true)}</td>
                        {cur.by_property.map((p) => {
                          const c = w.cells[p.property]
                          return (
                            <td key={p.property} style={{ ...TD, background: c ? (c.status === 'C' ? '#dcebdd' : '#e8f1e9') : (past ? '#faf8f2' : '#fff'), color: c ? '#0b3d2e' : '#b5ae9e' }}>
                              {c ? (
                                <span title={`Booking ${c.booking_ref} · arrives ${dmy(c.arrival_date)} · ${c.nights} nights · ${gbp2(c.price)} · ${statusLabel(c.status)}`}>
                                  <strong>{c.customer ?? c.booking_ref}</strong> <span style={{ color: '#6b6b6b' }}>· {c.booking_ref} · {gbp(c.price)}{c.nights !== 7 ? ` · ${c.nights} nights` : ''}{c.continues ? ' (cont.)' : ''}</span>
                                </span>
                              ) : 'free'}
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                  {cur.weeks.length === 0 && <tr><td style={TD} colSpan={4}>No weeks in the window.</td></tr>}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', maxHeight: '62vh', overflowY: 'auto', border: '1px solid #e5e1d6', borderRadius: 8, background: '#fff' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1300 }}>
                <thead><tr>
                  <th style={TH}>Arrival</th><th style={TH}>Property / item</th><th style={{ ...TH, ...R }}>Nights</th><th style={TH}>Booking</th><th style={TH}>Account</th>
                  <th style={{ ...TH, ...R }}>Adults</th><th style={{ ...TH, ...R }}>Children</th><th style={{ ...TH, ...R }}>Babies</th><th style={{ ...TH, ...R }}>Dogs</th><th style={TH}>Key code</th>
                  <th style={{ ...TH, ...R }}>Price</th><th style={{ ...TH, ...R }}>Paid so far</th><th style={{ ...TH, ...R }}>Outstanding</th><th style={TH}>Status</th><th style={TH}>Booked on</th><th style={TH}>Contact</th><th style={TH}>Notes</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => {
                    const due = Math.max(0, Math.round((r.booking_total - r.paid_so_far) * 100) / 100)
                    return (
                      <tr key={`${r.booking_ref}-${r.line_seq}`} style={{ color: r.is_let ? undefined : '#6b6b6b' }}>
                        <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.is_let ? dmy(r.arrival_date, true) : ''}</td>
                        <td style={{ ...TD, whiteSpace: 'nowrap', paddingLeft: r.is_let ? 8 : 24 }}>{r.is_let ? <strong>{SHORT[r.property] ?? r.property}</strong> : r.item}</td>
                        <td style={{ ...TD, ...R }}>{r.is_let ? r.nights : ''}</td>
                        <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.booking_ref}</td>
                        <td style={TD}>{r.customer_name || r.account_name || '—'}</td>
                        <td style={{ ...TD, ...R }}>{r.is_let ? r.adults : ''}</td><td style={{ ...TD, ...R }}>{r.is_let ? r.children : ''}</td><td style={{ ...TD, ...R }}>{r.is_let ? r.babies : ''}</td><td style={{ ...TD, ...R }}>{r.is_let ? r.dogs : ''}</td>
                        <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.is_let ? (r.key_code ?? '—') : ''}</td>
                        <td style={{ ...TD, ...R }}>{gbp2(r.price)}</td>
                        <td style={{ ...TD, ...R }}>{r.is_let ? gbp2(r.paid_so_far) : ''}</td>
                        <td style={{ ...TD, ...R, color: due > 0 ? '#c0392b' : undefined, fontWeight: due > 0 ? 600 : undefined }}>{r.is_let ? gbp2(due) : ''}</td>
                        <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.is_let ? statusLabel(r.status) : ''}{r.is_let && r.cancelled_on ? <span style={{ color: '#c0392b' }}> · cancelled {dmy(r.cancelled_on)}</span> : ''}</td>
                        <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.is_let ? dmy(r.booked_on) : ''}</td>
                        <td style={TD}>{r.is_let ? [r.mobile, r.home_tel, r.email].filter(Boolean).join(' · ') || '—' : ''}</td>
                        <td style={{ ...TD, maxWidth: 360 }}>{r.is_let ? (r.notes ?? '') : ''}</td>
                      </tr>
                    )
                  })}
                  {rows.length === 0 && !loading && <tr><td style={TD} colSpan={17}>No arrivals {dmy(from)} – {dmy(to)}{q ? ` matching “${q}”` : ''}.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
          {compare && ly && (
            <p style={{ fontSize: '0.78rem', color: '#8a8577', margin: '8px 0 0' }}>
              Last season ({ly.season}, arrivals {dmy(ly.from)} – {dmy(ly.to)}): {int(ly.totals.lets)} lets · {int(ly.totals.nights)} nights · {gbp(ly.totals.revenue)} let revenue · {gbp(ly.totals.extras)} extras.
            </p>
          )}
        </>
      )}
    </>
  )
}
